'use client';

import React, { useEffect, useRef, useState } from 'react';

export interface BetaGameDef {
  id: string;
  title: string;
  genre: 'FOOTBALL' | 'CRICKET' | 'BASKETBALL' | 'ARCADE' | 'CASUAL';
  description: string;
  version: string;
  playsCount: number;
  tags: string[];
  color: string;
  isBetaGame: true;
}

export const BETA_GAMES: BetaGameDef[] = [
  {
    id: 'beta-football-shootout',
    title: 'Turf Penalty Striker',
    genre: 'FOOTBALL',
    description: 'Aim and fire penalty kicks past the goalkeeper. Test your power, curve, and shot placement!',
    version: 'Beta 0.9.3',
    playsCount: 2480,
    tags: ['Penalty', 'Shootout', '3D Turf', 'Beta'],
    color: '#10B981',
    isBetaGame: true,
  },
  {
    id: 'beta-cricket-superover',
    title: 'Cricket Super Over',
    genre: 'CRICKET',
    description: '6 balls to chase down the target. Time your strokes to hit 4s, 6s, and win the local turf trophy!',
    version: 'Beta 0.9.4',
    playsCount: 3120,
    tags: ['Cricket', 'SuperOver', 'Batting', 'Beta'],
    color: '#F59E0B',
    isBetaGame: true,
  },
  {
    id: 'beta-hoops-3pointer',
    title: 'Cyber Hoops 3-Pointer',
    genre: 'BASKETBALL',
    description: 'Drag and release to sink deep 3-pointers. Build up heat streaks with consecutive swishes!',
    version: 'Beta 0.9.1',
    playsCount: 1940,
    tags: ['Basketball', 'Shooting', 'Physics', 'Beta'],
    color: '#EC4899',
    isBetaGame: true,
  },
  {
    id: 'beta-neon-pong',
    title: 'Neon Turf Pong',
    genre: 'ARCADE',
    description: 'Fast-paced cyber table tennis against high-speed AI paddles. Keep the ball alive as it speeds up!',
    version: 'Beta 0.9.5',
    playsCount: 4210,
    tags: ['Retro', 'Neon', 'TableTennis', 'Beta'],
    color: '#8B5CF6',
    isBetaGame: true,
  },
  {
    id: 'beta-field-dash',
    title: 'Reflex Turf Dash',
    genre: 'CASUAL',
    description: 'Endless top-down pitch runner! Steer your player, dodge defenders, and grab agility powerups.',
    version: 'Beta 0.9.0',
    playsCount: 1670,
    tags: ['Runner', 'Agility', 'Dodge', 'Beta'],
    color: '#3B82F6',
    isBetaGame: true,
  },
];

interface ArcadeBetaPlayerProps {
  game: BetaGameDef;
  onClose: () => void;
}

export default function ArcadeBetaPlayer({ game, onClose }: ArcadeBetaPlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameState, setGameState] = useState<'PLAYING' | 'GAMEOVER'>('PLAYING');
  const [gameMessage, setGameMessage] = useState('Game Started! Tap or click to play.');

  // Play audio blip using Web Audio API
  const playBeep = (freq = 440, type: OscillatorType = 'sine', duration = 0.08) => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch {}
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let isRunning = true;

    // Canvas Dimensions
    canvas.width = 640;
    canvas.height = 420;

    let currentScore = 0;
    let ballsLeft = 6; // for cricket
    let streak = 0;

    // Dispatch score helper
    const updateScore = (pts: number) => {
      currentScore += pts;
      setScore(currentScore);
      setHighScore(prev => Math.max(prev, currentScore));
      window.postMessage({ type: 'KHELDROP_SCORE', score: currentScore, gameId: game.id }, '*');
    };

    // ----------------------------------------------------
    // GAME 1: TURF PENALTY STRIKER (Football)
    // ----------------------------------------------------
    if (game.id === 'beta-football-shootout') {
      let goalieX = 320;
      let goalieDir = 2.8;
      let ballX = 320;
      let ballY = 360;
      let ballRadius = 14;
      let isShot = false;
      let shotTargetX = 320;
      let shotTargetY = 130;
      let shotsCount = 0;

      const clickHandler = (e: MouseEvent) => {
        if (isShot) return;
        const rect = canvas.getBoundingClientRect();
        shotTargetX = (e.clientX - rect.left) * (canvas.width / rect.width);
        shotTargetY = (e.clientY - rect.top) * (canvas.height / rect.height);

        // Clamp to goal boundaries
        shotTargetX = Math.max(140, Math.min(500, shotTargetX));
        shotTargetY = Math.max(90, Math.min(220, shotTargetY));

        isShot = true;
        shotsCount++;
        playBeep(260, 'triangle', 0.12);
      };

      canvas.addEventListener('click', clickHandler);

      const loop = () => {
        if (!isRunning) return;

        // Render Pitch
        ctx.fillStyle = '#064E3B';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Turf Strips
        for (let i = 0; i < canvas.height; i += 40) {
          ctx.fillStyle = i % 80 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.05)';
          ctx.fillRect(0, i, canvas.width, 40);
        }

        // Goal Post
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 6;
        ctx.strokeRect(120, 80, 400, 150);

        // Goal Netting
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        for (let gx = 130; gx < 520; gx += 20) {
          ctx.beginPath();
          ctx.moveTo(gx, 80);
          ctx.lineTo(gx, 230);
          ctx.stroke();
        }
        for (let gy = 90; gy < 230; gy += 15) {
          ctx.beginPath();
          ctx.moveTo(120, gy);
          ctx.lineTo(520, gy);
          ctx.stroke();
        }

        // Penalty Spot
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(320, 360, 5, 0, Math.PI * 2);
        ctx.fill();

        // Goalkeeper
        goalieX += goalieDir;
        if (goalieX > 460 || goalieX < 180) goalieDir *= -1;

        ctx.fillStyle = '#EF4444';
        ctx.beginPath();
        ctx.arc(goalieX, 190, 22, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Goalie Gloves
        ctx.fillStyle = '#F59E0B';
        ctx.beginPath();
        ctx.arc(goalieX - 26, 185, 9, 0, Math.PI * 2);
        ctx.arc(goalieX + 26, 185, 9, 0, Math.PI * 2);
        ctx.fill();

        // Ball Animation
        if (isShot) {
          ballX += (shotTargetX - ballX) * 0.15;
          ballY += (shotTargetY - ballY) * 0.15;
          ballRadius = Math.max(7, ballRadius - 0.25);

          if (Math.abs(ballY - shotTargetY) < 4) {
            // Check collision with goalie
            const distToGoalkeep = Math.hypot(ballX - goalieX, ballY - 190);
            if (distToGoalkeep < 36) {
              // SAVED!
              playBeep(180, 'sawtooth', 0.2);
              setGameMessage('SAVED by Goalkeeper! Try again.');
            } else {
              // GOAL!
              playBeep(580, 'sine', 0.25);
              updateScore(100);
              streak++;
              setGameMessage(`GOAL!! Striker Streak x${streak}! (+100 pts)`);
            }

            // Reset ball
            setTimeout(() => {
              ballX = 320;
              ballY = 360;
              ballRadius = 14;
              isShot = false;
            }, 650);
          }
        }

        // Draw Football
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(ballX, ballY, ballRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Instructions
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '13px Inter, sans-serif';
        ctx.fillText('Click anywhere inside the goal to shoot!', 20, 30);
        ctx.fillText(`Shots: ${shotsCount}`, 540, 30);

        animationFrameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        isRunning = false;
        canvas.removeEventListener('click', clickHandler);
        cancelAnimationFrame(animationFrameId);
      };
    }

    // ----------------------------------------------------
    // GAME 2: CRICKET SUPER OVER
    // ----------------------------------------------------
    if (game.id === 'beta-cricket-superover') {
      let ballX = 320;
      let ballY = 80;
      let ballSpeed = 3.5;
      let isBowling = true;
      let batsmanBatAngle = 0;
      let matchOver = false;

      const hitHandler = () => {
        if (!isBowling || matchOver) return;

        // Sweet spot is ballY between 290 and 340
        if (ballY >= 285 && ballY <= 345) {
          const accuracy = Math.abs(ballY - 315);
          if (accuracy < 8) {
            // SIX!
            playBeep(650, 'square', 0.2);
            updateScore(6);
            setGameMessage('MAXIMUM! 6 RUNS! Smashing pull over deep midwicket!');
          } else if (accuracy < 18) {
            // FOUR!
            playBeep(520, 'sine', 0.15);
            updateScore(4);
            setGameMessage('CRACKING FOUR! Driven through the covers!');
          } else {
            // 2 Runs
            playBeep(380, 'sine', 0.1);
            updateScore(2);
            setGameMessage('Quick double taken! Good placement.');
          }
        } else if (ballY > 345) {
          // MISSED / BOWLED!
          playBeep(160, 'sawtooth', 0.25);
          setGameMessage('BOWLED! Timber disturbed! Stumps cartwheeling.');
        } else {
          // EARLY SHOT
          playBeep(220, 'sawtooth', 0.12);
          setGameMessage('Early shot! Edged and beaten by pace.');
        }

        batsmanBatAngle = -0.5;
        isBowling = false;
        ballsLeft--;

        setTimeout(() => {
          batsmanBatAngle = 0;
          if (ballsLeft <= 0) {
            matchOver = true;
            setGameState('GAMEOVER');
            setGameMessage(`Super Over Complete! Final Score: ${currentScore} Runs`);
          } else {
            ballX = 320 + (Math.random() * 40 - 20);
            ballY = 80;
            isBowling = true;
          }
        }, 800);
      };

      canvas.addEventListener('click', hitHandler);

      const loop = () => {
        if (!isRunning) return;

        // Pitch Ground
        ctx.fillStyle = '#D97706';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Turf Grass Surrounding
        ctx.fillStyle = '#059669';
        ctx.fillRect(0, 0, 180, canvas.height);
        ctx.fillRect(460, 0, 180, canvas.height);

        // Pitch Crease lines
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(180, 100);
        ctx.lineTo(460, 100); // Bowling crease
        ctx.moveTo(180, 320);
        ctx.lineTo(460, 320); // Popping crease
        ctx.stroke();

        // Stumps
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(312, 335, 4, 25);
        ctx.fillRect(320, 335, 4, 25);
        ctx.fillRect(328, 335, 4, 25);

        // Bowling Motion
        if (isBowling && !matchOver) {
          ballY += ballSpeed;
          if (ballY > 360) {
            hitHandler();
          }
        }

        // Draw Cricket Ball (Red cherry)
        ctx.fillStyle = '#DC2626';
        ctx.beginPath();
        ctx.arc(ballX, ballY, 11, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Batsman Silhouette
        ctx.fillStyle = '#1E3A8A';
        ctx.beginPath();
        ctx.arc(320, 305, 14, 0, Math.PI * 2); // Helmet
        ctx.fill();

        // Bat
        ctx.save();
        ctx.translate(320, 310);
        ctx.rotate(batsmanBatAngle);
        ctx.fillStyle = '#F59E0B';
        ctx.fillRect(-6, 0, 12, 32);
        ctx.restore();

        // HUD
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '13px Inter, sans-serif';
        ctx.fillText(`Balls Remaining: ${ballsLeft}/6`, 20, 30);
        ctx.fillText(`Target: 18 Runs`, 520, 30);

        animationFrameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        isRunning = false;
        canvas.removeEventListener('click', hitHandler);
        cancelAnimationFrame(animationFrameId);
      };
    }

    // ----------------------------------------------------
    // GAME 3: CYBER HOOPS 3-POINTER
    // ----------------------------------------------------
    if (game.id === 'beta-hoops-3pointer') {
      let ballX = 320;
      let ballY = 350;
      let ballVx = 0;
      let ballVy = 0;
      let isFlying = false;
      let hoopX = 320;
      let hoopY = 110;
      let hoopSpeed = 2;

      let isDragging = false;
      let dragStartX = 0;
      let dragStartY = 0;
      let dragCurrentX = 0;
      let dragCurrentY = 0;

      const mousedown = (e: MouseEvent) => {
        if (isFlying) return;
        const rect = canvas.getBoundingClientRect();
        dragStartX = (e.clientX - rect.left) * (canvas.width / rect.width);
        dragStartY = (e.clientY - rect.top) * (canvas.height / rect.height);
        dragCurrentX = dragStartX;
        dragCurrentY = dragStartY;
        isDragging = true;
      };

      const mousemove = (e: MouseEvent) => {
        if (!isDragging) return;
        const rect = canvas.getBoundingClientRect();
        dragCurrentX = (e.clientX - rect.left) * (canvas.width / rect.width);
        dragCurrentY = (e.clientY - rect.top) * (canvas.height / rect.height);
      };

      const mouseup = () => {
        if (!isDragging || isFlying) return;
        isDragging = false;
        const dx = dragStartX - dragCurrentX;
        const dy = dragStartY - dragCurrentY;

        ballVx = dx * 0.12;
        ballVy = dy * 0.14;
        isFlying = true;
        playBeep(320, 'sine', 0.1);
      };

      canvas.addEventListener('mousedown', mousedown);
      canvas.addEventListener('mousemove', mousemove);
      canvas.addEventListener('mouseup', mouseup);

      const loop = () => {
        if (!isRunning) return;

        // Court
        ctx.fillStyle = '#0F172A';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Court Lines
        ctx.strokeStyle = '#38BDF8';
        ctx.lineWidth = 2;
        ctx.strokeRect(100, 40, 440, 340);
        ctx.beginPath();
        ctx.arc(320, 110, 80, 0, Math.PI);
        ctx.stroke();

        // Moving Hoop
        hoopX += hoopSpeed;
        if (hoopX > 440 || hoopX < 200) hoopSpeed *= -1;

        // Backboard
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(hoopX - 35, hoopY - 30, 70, 6);
        // Rim
        ctx.strokeStyle = '#F97316';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(hoopX, hoopY, 22, 0, Math.PI * 2);
        ctx.stroke();

        // Net
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(hoopX - 18, hoopY);
        ctx.lineTo(hoopX - 10, hoopY + 28);
        ctx.lineTo(hoopX + 10, hoopY + 28);
        ctx.lineTo(hoopX + 18, hoopY);
        ctx.stroke();

        // Physics
        if (isFlying) {
          ballX += ballVx;
          ballY += ballVy;
          ballVy += 0.4; // Gravity

          // Check Hoop score
          const distToHoop = Math.hypot(ballX - hoopX, ballY - hoopY);
          if (distToHoop < 22 && ballVy > 0) {
            playBeep(580, 'triangle', 0.2);
            updateScore(3);
            setGameMessage('SWISH! Deep 3-Pointer Sunk! (+3 pts)');
            isFlying = false;
            ballX = 320;
            ballY = 350;
            ballVx = 0;
            ballVy = 0;
          }

          // Reset on miss / bottom screen
          if (ballY > 440 || ballX < 0 || ballX > 640) {
            isFlying = false;
            ballX = 320;
            ballY = 350;
            ballVx = 0;
            ballVy = 0;
            setGameMessage('Missed! Pull down and release to adjust angle.');
          }
        }

        // Drag Trajectory Guide
        if (isDragging) {
          ctx.strokeStyle = '#F43F5E';
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(ballX, ballY);
          ctx.lineTo(ballX + (dragStartX - dragCurrentX) * 2, ballY + (dragStartY - dragCurrentY) * 2);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // Draw Basketball
        ctx.fillStyle = '#EA580C';
        ctx.beginPath();
        ctx.arc(ballX, ballY, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '13px Inter, sans-serif';
        ctx.fillText('Drag back from the ball to aim & release!', 20, 30);

        animationFrameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        isRunning = false;
        canvas.removeEventListener('mousedown', mousedown);
        canvas.removeEventListener('mousemove', mousemove);
        canvas.removeEventListener('mouseup', mouseup);
        cancelAnimationFrame(animationFrameId);
      };
    }

    // ----------------------------------------------------
    // GAME 4: NEON TURF PONG
    // ----------------------------------------------------
    if (game.id === 'beta-neon-pong') {
      let playerY = 170;
      let aiY = 170;
      let ballX = 320;
      let ballY = 210;
      let bSpeedX = 4;
      let bSpeedY = 2.5;

      const mousemove = (e: MouseEvent) => {
        const rect = canvas.getBoundingClientRect();
        const clientY = (e.clientY - rect.top) * (canvas.height / rect.height);
        playerY = Math.max(10, Math.min(330, clientY - 40));
      };

      canvas.addEventListener('mousemove', mousemove);

      const loop = () => {
        if (!isRunning) return;

        // Dark Court
        ctx.fillStyle = '#090A0F';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Center line
        ctx.strokeStyle = 'rgba(124, 58, 237, 0.3)';
        ctx.setLineDash([8, 8]);
        ctx.beginPath();
        ctx.moveTo(320, 0);
        ctx.lineTo(320, 420);
        ctx.stroke();
        ctx.setLineDash([]);

        // AI movement
        aiY += (ballY - (aiY + 40)) * 0.08;
        aiY = Math.max(10, Math.min(330, aiY));

        // Ball movement
        ballX += bSpeedX;
        ballY += bSpeedY;

        // Wall collisions
        if (ballY < 12 || ballY > 408) {
          bSpeedY *= -1;
          playBeep(300, 'sine', 0.05);
        }

        // Player Paddle collision (Left)
        if (ballX < 36 && ballX > 18 && ballY > playerY && ballY < playerY + 80) {
          bSpeedX = Math.abs(bSpeedX) * 1.05;
          bSpeedY += (ballY - (playerY + 40)) * 0.1;
          playBeep(480, 'sine', 0.08);
          updateScore(10);
        }

        // AI Paddle collision (Right)
        if (ballX > 604 && ballX < 622 && ballY > aiY && ballY < aiY + 80) {
          bSpeedX = -Math.abs(bSpeedX) * 1.03;
          playBeep(420, 'sine', 0.08);
        }

        // Out of bounds
        if (ballX < 0) {
          // AI scores
          playBeep(180, 'sawtooth', 0.2);
          setGameMessage('Point to Opponent! Move mouse to track ball.');
          ballX = 320;
          ballY = 210;
          bSpeedX = 4;
        } else if (ballX > 640) {
          // Player scores!
          playBeep(640, 'sine', 0.15);
          updateScore(50);
          setGameMessage('ACE! Beautiful rally winner! (+50 pts)');
          ballX = 320;
          ballY = 210;
          bSpeedX = -4;
        }

        // Draw Player Paddle
        ctx.fillStyle = '#8B5CF6';
        ctx.shadowBlur = 14;
        ctx.shadowColor = '#8B5CF6';
        ctx.fillRect(18, playerY, 12, 80);

        // Draw AI Paddle
        ctx.fillStyle = '#EF4444';
        ctx.shadowColor = '#EF4444';
        ctx.fillRect(610, aiY, 12, 80);

        // Draw Ball
        ctx.fillStyle = '#FFFFFF';
        ctx.shadowColor = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(ballX, ballY, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        animationFrameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        isRunning = false;
        canvas.removeEventListener('mousemove', mousemove);
        cancelAnimationFrame(animationFrameId);
      };
    }

    // ----------------------------------------------------
    // GAME 5: REFLEX TURF DASH
    // ----------------------------------------------------
    if (game.id === 'beta-field-dash') {
      let runnerX = 320;
      let obstacles: { x: number; y: number; speed: number; w: number }[] = [];
      let frame = 0;

      const mousemove = (e: MouseEvent) => {
        const rect = canvas.getBoundingClientRect();
        runnerX = Math.max(30, Math.min(610, (e.clientX - rect.left) * (canvas.width / rect.width)));
      };

      canvas.addEventListener('mousemove', mousemove);

      const loop = () => {
        if (!isRunning) return;
        frame++;

        // Pitch
        ctx.fillStyle = '#047857';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Spawn obstacles
        if (frame % 45 === 0) {
          obstacles.push({
            x: Math.random() * 560 + 40,
            y: -20,
            speed: 3.5 + Math.random() * 2,
            w: 36,
          });
        }

        // Move and draw obstacles (Defenders)
        ctx.fillStyle = '#EF4444';
        for (let i = obstacles.length - 1; i >= 0; i--) {
          const obs = obstacles[i];
          obs.y += obs.speed;

          ctx.beginPath();
          ctx.arc(obs.x, obs.y, 16, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Collision detection
          if (Math.hypot(runnerX - obs.x, 370 - obs.y) < 30) {
            playBeep(160, 'sawtooth', 0.25);
            setGameState('GAMEOVER');
            setGameMessage(`Tackled by defender! Final Dash Score: ${currentScore}`);
            isRunning = false;
            return;
          }

          if (obs.y > 440) {
            obstacles.splice(i, 1);
            updateScore(15);
          }
        }

        // Runner
        ctx.fillStyle = '#3B82F6';
        ctx.beginPath();
        ctx.arc(runnerX, 370, 18, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3;
        ctx.stroke();

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '13px Inter, sans-serif';
        ctx.fillText('Move mouse horizontally to dodge red defenders!', 20, 30);

        animationFrameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        isRunning = false;
        canvas.removeEventListener('mousemove', mousemove);
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [game.id]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#0B0D12',
      color: '#FFFFFF',
    }}>
      {/* Top Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: '#0F1117',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontSize: '17px', fontWeight: 800 }}>{game.title}</span>
          <span style={{
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '6px',
            background: 'rgba(124, 58, 237, 0.2)',
            color: '#A78BFA',
            border: '1px solid rgba(124, 58, 237, 0.4)',
          }}>
            {game.version}
          </span>
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '6px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#34D399',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            fontWeight: 600,
          }}>
            Canvas WebGL Sandbox
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase' }}>Current Score: </span>
            <span style={{ fontSize: '18px', fontWeight: 800, color: '#38BDF8', marginLeft: '6px' }}>{score}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              color: '#FFFFFF',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Exit Game
          </button>
        </div>
      </div>

      {/* Game Canvas Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        position: 'relative',
        background: '#06070A',
      }}>
        <div style={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto',
              cursor: 'crosshair',
            }}
          />

          {gameState === 'GAMEOVER' && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              textAlign: 'center',
            }}>
              <h2 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 10px', color: '#EF4444' }}>
                MATCH OVER
              </h2>
              <p style={{ fontSize: '16px', color: '#E2E8F0', margin: '0 0 8px' }}>
                {gameMessage}
              </p>
              <p style={{ fontSize: '22px', fontWeight: 800, color: '#38BDF8', margin: '0 0 24px' }}>
                Final Score: {score}
              </p>
              <button
                type="button"
                onClick={() => {
                  setGameState('PLAYING');
                  setScore(0);
                  setGameMessage('Game restarted! Let\'s go!');
                }}
                style={{
                  background: '#FFFFFF',
                  color: '#000000',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: 800,
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Play Again
              </button>
            </div>
          )}
        </div>

        {/* Live Feedback Toast */}
        <div style={{
          marginTop: '16px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '8px 18px',
          fontSize: '13px',
          color: 'rgba(255, 255, 255, 0.8)',
          fontWeight: 500,
        }}>
          {gameMessage}
        </div>
      </div>
    </div>
  );
}
